import React, { useEffect, useState,forwardRef } from "react";
import {
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material";
import {
    CartesianGrid,
    Legend,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
} from "recharts";

const COLORS = ["#1976d2", "#2e7d32", "#ed6c02"];

export const SalesReport = ({ reportRange, rawData }) => {
    const theme = useTheme();
    const [chartData, setChartData] = useState([]);
    const [pieData, setPieData] = useState([]);
    const [orderSummary, setOrderSummary] = useState({});
    const [paymentSummary, setPaymentSummary] = useState({});
    const [cashDropTotal, setCashDropTotal] = useState(0);
    const [paidOutTotal, setPaidOutTotal] = useState(0);
    const [salesSummary, setSalesSummary] = useState({});

  useEffect(() => {
    // Correctly handle missing rawData to prevent errors
    if (!rawData) {
        console.log("Didn't find any Data to move Forward...");
        setChartData([]);
        setPieData([]);
        setOrderSummary({});
        setPaymentSummary({});
        setCashDropTotal(0);
        setPaidOutTotal(0);
        setSalesSummary({});
        return;
    }

    const hourlySales = {};
    const paymentMap = { Cash: 0, "Credit Card": 0, "Gift Card": 0, Online: 0, Other: 0 };
    let tipTotal = 0;
    let completed = 0;
    
    // Use optional chaining for safe access
    const voided = rawData.cancelOrders?.length ?? 0;
    const orderTypeMap = { "In-store": 0, Pickup: 0, Kiosk: 0, Online: 0, Other: 0 };

    // Use a null check on the loop itself
    for (let order of rawData.completedOrders || []) {
        const hour = new Date(order.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        hourlySales[hour] = (hourlySales[hour] || 0) + (order.total ?? 0);
        tipTotal += order.Tip ?? 0;
        completed++;

        // Use a null check on the inner loop
        for (let payment of order.payment || []) {
            switch (payment.payType) {
                case 0: paymentMap["Cash"] += (payment.amount ?? 0); break;
                case 1: paymentMap["Gift Card"] += (payment.amount ?? 0); break;
                case 2: paymentMap["Online"] += (payment.amount ?? 0); break;
                case 3: paymentMap["Credit Card"] += (payment.amount ?? 0); break;
                default: paymentMap["Other"] += (payment.amount ?? 0);
            }
        }

        const typeName =
            order.type === 0 ? "In-store" :
            order.type === 1 ? "Pickup" :
            order.type === 2 ? "Kiosk" :
            order.type === 3 ? "Online" : "Other";
        orderTypeMap[typeName] = (orderTypeMap[typeName] || 0) + 1;
    }

    const cashDropSum = (rawData.cashDrops || []).reduce((acc, drop) => acc + (drop.amount ?? 0), 0);
    const paidOutSum = (rawData.paidOuts || []).reduce((acc, payout) => acc + (payout.amount ?? 0), 0);

    setChartData(Object.entries(hourlySales).map(([date, total]) => ({ date, total })));
    setPieData(Object.entries(paymentMap).map(([name, value]) => ({ name, value })));
    setOrderSummary({ ...orderTypeMap, Completed: completed, Void: voided });
    setPaymentSummary(paymentMap);
    setCashDropTotal(cashDropSum);
    setPaidOutTotal(paidOutSum);

    const expectedCash = (rawData.openingCash ?? 0) + (paymentMap["Cash"] ?? 0) - cashDropSum - paidOutSum;
    const difference = (rawData.tillCash ?? 0) - expectedCash;

    setSalesSummary({
        subTotal: rawData.totalSales ?? 0,
        tax: rawData.totalTax ?? 0, // Assuming a totalTax property might exist
        total: rawData.totalSales ?? 0,
        tips: tipTotal,
        openingCash: rawData.openingCash ?? 0,
        cashCount: rawData.tillCash ?? 0,
        overShort: difference.toFixed(2)
    });

}, [reportRange, rawData]);


    return (
        <Container sx={{ mt: 4, mb: 4 }} >
            <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#f5faff" }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                    Sales Overview
                </Typography>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <LineChart width={400} height={250} data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="total" stroke={theme.palette.primary.main} />
                        </LineChart>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <PieChart width={300} height={250}>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#fff8e1" }}>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            Order Summary
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Order Type</TableCell>
                                    <TableCell align="right">Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(orderSummary).map(([type, count]) => (
                                    <TableRow key={type}>
                                        <TableCell>{type}</TableCell>
                                        <TableCell align="right">{count}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#e1f5fe" }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Payment Summary
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Payment Type</TableCell>
                                    <TableCell align="right">Amount ($)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(paymentSummary).map(([type, amount]) => (
                                    <TableRow key={type}>
                                        <TableCell>{type}</TableCell>
                                        {/* Use optional chaining for safe number formatting */}
                                        <TableCell align="right">${amount?.toFixed(2) ?? '0.00'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        <br />

                        <Typography variant="subtitle2">Total Cash Drops : ${cashDropTotal?.toFixed(2) ?? '0.00'}</Typography>

                        <Typography variant="subtitle2" sx={{ mt: 1 }}>
                            Total Paid Outs : ${paidOutTotal?.toFixed(2) ?? '0.00'}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: "#f3e5f5" }}>
                <Typography variant="h6" color="secondary" gutterBottom>
                    Sales Summary
                </Typography>
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell>Sub Total</TableCell>
                            <TableCell align="right">${salesSummary.subTotal?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Tax</TableCell>
                            <TableCell align="right">${salesSummary.tax?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Total Sale</TableCell>
                            <TableCell align="right">${salesSummary.total?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Tips</TableCell>
                            <TableCell align="right">${salesSummary.tips?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Opening Cash</TableCell>
                            <TableCell align="right">${salesSummary.openingCash?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Cash Count</TableCell>
                            <TableCell align="right">${salesSummary.cashCount?.toFixed(2) ?? '0.00'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Over/Short</TableCell>
                            <TableCell align="right" sx={{ color: "error.main" }}>
                                ${salesSummary.overShort ?? '0.00'}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Paper>
        </Container>
    );
};